import type { FirebaseError } from 'firebase/app';
import type { AuthCredential } from 'firebase/auth';
import type { OAuthProviderId } from '../core/models/PlatformConfig';
import type { AccountExistsError, AuthProviderInfo } from './authTypes';
import { listProviders } from './authHelpers';
import { syncProfileAfterSignIn } from './authHelpers';
import {
  finishPrimarySignIn,
  loadAuth,
  mapFirebaseUser,
  wrapMfaError,
  type MultiFactorResolverFn,
} from './firebaseAuthSession';
import { markEmailMfaGatePending } from './emailMfaSession';

/** sessionStorage key: expected email while a Google link redirect is in flight. */
const PENDING_GOOGLE_LINK_KEY = 'alea.pendingGoogleLink';

function buildOAuthProvider(
  providerId: OAuthProviderId,
  {
    GoogleAuthProvider,
    FacebookAuthProvider,
    OAuthProvider,
  }: {
    GoogleAuthProvider: typeof import('firebase/auth').GoogleAuthProvider;
    FacebookAuthProvider: typeof import('firebase/auth').FacebookAuthProvider;
    OAuthProvider: typeof import('firebase/auth').OAuthProvider;
  },
) {
  if (providerId === 'google') {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return provider;
  }
  if (providerId === 'facebook') {
    return new FacebookAuthProvider();
  }
  if (providerId === 'apple') {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return provider;
  }
  const provider = new OAuthProvider('microsoft.com');
  provider.addScope('email');
  return provider;
}

function credentialFromOAuthError(
  providerId: OAuthProviderId,
  err: FirebaseError,
  { GoogleAuthProvider, FacebookAuthProvider, OAuthProvider }: {
    GoogleAuthProvider: typeof import('firebase/auth').GoogleAuthProvider;
    FacebookAuthProvider: typeof import('firebase/auth').FacebookAuthProvider;
    OAuthProvider: typeof import('firebase/auth').OAuthProvider;
  },
) {
  if (providerId === 'google') return GoogleAuthProvider.credentialFromError(err);
  if (providerId === 'facebook') return FacebookAuthProvider.credentialFromError(err);
  return OAuthProvider.credentialFromError(err);
}

export async function linkPendingOAuthCredential(pendingCredential: AuthCredential | null): Promise<void> {
  if (!pendingCredential) return;
  const { auth, linkWithCredential } = await loadAuth();
  if (!auth.currentUser) return;
  await linkWithCredential(auth.currentUser, pendingCredential);
}

async function assertLinkedGoogleEmailMatches(expectedEmail: string): Promise<void> {
  const { auth, GoogleAuthProvider, unlink } = await loadAuth();
  const user = auth.currentUser;
  if (!user) return;

  // God may link any Google account (bootstrap email is often not a Google Workspace inbox).
  const claims = await user.getIdTokenResult().then((r) => r.claims).catch(() => ({}));
  if (String((claims as { role?: string }).role ?? '') === 'god') return;

  if (typeof user.reload === 'function') {
    await user.reload();
  }
  const googleProvider = user.providerData.find((provider) => provider.providerId === 'google.com');
  const googleEmail = String(googleProvider?.email ?? user.email ?? '').trim().toLowerCase();
  const expected = expectedEmail.trim().toLowerCase();

  if (expected && googleEmail && googleEmail !== expected) {
    await unlink(user, GoogleAuthProvider.PROVIDER_ID);
    throw Object.assign(
      new Error('The Google account must use the same email your banker registered.'),
      { code: 'auth/google-email-mismatch' },
    );
  }
}

async function syncProfileAfterGoogleLink(): Promise<void> {
  try {
    const { syncRegisteredProfile } = await import('./firebase/FunctionsService.js');
    await syncRegisteredProfile();
  } catch {
    // Profile sync is best-effort after linking
  }
}

export async function signInWithOAuth(providerId: OAuthProviderId) {
  const {
    auth,
    GoogleAuthProvider,
    FacebookAuthProvider,
    OAuthProvider,
    signInWithPopup,
    getMultiFactorResolver,
    fetchSignInMethodsForEmail,
  } = await loadAuth();
  const provider = buildOAuthProvider(providerId, {
    GoogleAuthProvider,
    FacebookAuthProvider,
    OAuthProvider,
  });

  try {
    await signInWithPopup(auth, provider);
    const uid = auth.currentUser?.uid?.trim();
    if (uid) markEmailMfaGatePending(uid);
    const email = auth.currentUser?.email ?? '';
    return finishPrimarySignIn(email);
  } catch (err: unknown) {
    const fbErr = err as FirebaseError & { customData?: { email?: string } };
    if (fbErr?.code === 'auth/multi-factor-auth-required') {
      wrapMfaError(err, auth, getMultiFactorResolver as MultiFactorResolverFn);
    }
    if (fbErr?.code === 'auth/account-exists-with-different-credential') {
      const email = String(fbErr.customData?.email ?? '');
      const pendingCredential = credentialFromOAuthError(providerId, fbErr, {
        GoogleAuthProvider,
        FacebookAuthProvider,
        OAuthProvider,
      });
      const methods = email
        ? await fetchSignInMethodsForEmail(auth, email)
        : [];
      const linkError: AccountExistsError = {
        code: 'account-exists',
        email,
        pendingCredential,
        methods,
      };
      throw linkError;
    }
    throw err;
  }
}

export async function signInWithGoogle() {
  return signInWithOAuth('google');
}

export async function completeGoogleRedirect() {
  const { auth, getRedirectResult } = await loadAuth();
  const result = await getRedirectResult(auth);
  const pendingEmail = sessionStorage.getItem(PENDING_GOOGLE_LINK_KEY);
  if (pendingEmail != null) {
    sessionStorage.removeItem(PENDING_GOOGLE_LINK_KEY);
    if (result?.user) {
      await assertLinkedGoogleEmailMatches(pendingEmail);
      await syncProfileAfterGoogleLink();
      await result.user.getIdToken(true);
    }
  }
  if (!result?.user) return null;
  return syncProfileAfterSignIn(mapFirebaseUser, auth);
}

export async function signInAndLinkGoogle(
  email: string,
  password: string,
  pendingCredential: AuthCredential | null,
) {
  const { auth, signInWithEmailAndPassword, linkWithCredential, getMultiFactorResolver } =
    await loadAuth();
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const uid = credential.user?.uid?.trim();
    if (uid) markEmailMfaGatePending(uid);
    if (pendingCredential) {
      await linkWithCredential(credential.user, pendingCredential);
      await assertLinkedGoogleEmailMatches(email);
    }
    return finishPrimarySignIn(email);
  } catch (err) {
    wrapMfaError(err, auth, getMultiFactorResolver as MultiFactorResolverFn);
  }
}

/**
 * Links Google via popup. Falls back to full-page redirect if the browser blocks the popup.
 */
export async function linkGoogleAccount(): Promise<AuthProviderInfo[]> {
  const { auth, GoogleAuthProvider, linkWithPopup, linkWithRedirect } = await loadAuth();
  if (!auth.currentUser) throw new Error('Not signed in');
  const expectedEmail = String(auth.currentUser.email ?? '').trim().toLowerCase();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  try {
    await linkWithPopup(auth.currentUser, provider);
  } catch (err: unknown) {
    const code = (err as { code?: string })?.code;
    if (code !== 'auth/popup-blocked') throw err;
    sessionStorage.setItem(PENDING_GOOGLE_LINK_KEY, expectedEmail);
    await linkWithRedirect(auth.currentUser, provider);
    return getLinkedProviders();
  }

  await assertLinkedGoogleEmailMatches(expectedEmail);
  await syncProfileAfterGoogleLink();
  await auth.currentUser.getIdToken(true);
  return getLinkedProviders();
}

export async function unlinkGoogleAccount(): Promise<AuthProviderInfo[]> {
  const { auth, GoogleAuthProvider, unlink } = await loadAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  await unlink(user, GoogleAuthProvider.PROVIDER_ID);
  try {
    const { syncRegisteredProfile } = await import('./firebase/FunctionsService.js');
    await syncRegisteredProfile();
  } catch {
    // Profile sync is best-effort after unlinking (clears googleLinked)
  }
  await user.getIdToken(true);
  return getLinkedProviders();
}

export async function getLinkedProviders(): Promise<AuthProviderInfo[]> {
  const { auth } = await loadAuth();
  return listProviders(auth.currentUser);
}
